import { AdditionalEntitlement, ReimbursementIndication } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { TranslateFunction } from '@dreryk/react-common';

import {
   shouldDistinctReimbursementByChosenEntitlementTypes,
   shouldDistinctReimbursementByVisitDiagnoses,
} from '~libs.shared.prescription';
import { ReimbursementTooltipContent } from '~libs.desktop.shared.medicine';

import { draftPrescriptionsTranslationKeys } from '../../../translations';
export class ReimbursementTooltipContentBuilder {
   private description = '';
   private distinctByVisitDiagnoses = false;
   private distinctByChosenEntitlements = false;

   constructor(
      private extendedMedicineDatabaseAvailable: boolean,
      private translate: TranslateFunction,
      private reimbursementIndication: ReimbursementIndication
   ) {
      this.description = reimbursementIndication.value;
   }

   withDistinctByVisitDiagnoses(visitDiagnoses: VisitDiagnosis[]): this {
      if (!this.extendedMedicineDatabaseAvailable) {
         return this;
      }
      const distinctByVisitDiagnoses = shouldDistinctReimbursementByVisitDiagnoses(
         this.reimbursementIndication.eligibleIcd10Codes,
         visitDiagnoses
      );
      if (distinctByVisitDiagnoses) {
         this.distinctByVisitDiagnoses = true;
         this.description += ` ${this.translate(
            draftPrescriptionsTranslationKeys.reimbursementIndication.chosenOnTheVisit
         )}`;
      }
      return this;
   }

   withDistinctByChosenEntitlements(selectedAdditionalEntitlements: AdditionalEntitlement[]): this {
      if (!this.extendedMedicineDatabaseAvailable) {
         return this;
      }
      const distinctByChosenEntitlements = shouldDistinctReimbursementByChosenEntitlementTypes(
         this.reimbursementIndication.eligibleEntitlement,
         selectedAdditionalEntitlements
      );

      if (distinctByChosenEntitlements) {
         this.distinctByChosenEntitlements = true;
         this.description += ` ${this.translate(draftPrescriptionsTranslationKeys.reimbursementIndication.active)}`;
      }
      return this;
   }

   withStatutorilyDesignatedFreeEntitlementsAnnotation(): this {
      const { statutorilyDesignatedFreeEntitlements } = this.reimbursementIndication;
      if (statutorilyDesignatedFreeEntitlements && statutorilyDesignatedFreeEntitlements.length > 0) {
         this.description += ` (${statutorilyDesignatedFreeEntitlements
            .map((entitlement) =>
               this.translate(
                  draftPrescriptionsTranslationKeys.reimbursementIndication.statutorilyDesignatedFreeEntitlements[
                     entitlement as 'S' | 'DZ' | 'C'
                  ]
               )
            )
            .join(' ')})`;
      }
      return this;
   }

   withEligibleIcd10Codes(): this {
      const { eligibleIcd10Codes } = this.reimbursementIndication;
      if (eligibleIcd10Codes.length > 0) {
         this.description += ` (${eligibleIcd10Codes.join(', ')})`;
      }
      return this;
   }

   build(): ReimbursementTooltipContent {
      return {
         description: this.description,
         distinct: this.distinctByVisitDiagnoses || this.distinctByChosenEntitlements,
      };
   }
}
